import { ForgotPasswordModal } from "@/components/auth/ForgotPasswordModal";
import LanguageSelector from "@/components/common/LanguageSelector";
import CommonButton from "@/components/utils/CommonButton";
import CommonFormControl from "@/components/utils/CommonFormControl";
import { Colors } from "@/enums/Colors";
import { LazoPaths } from "@/enums/LazoPaths";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import environment from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/environment.json";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { useRequestMagicLink } from "@/hooks/queries/auth/useAuthQueries";
import LazoAuthService from "@/services/LazoAuthService";
import { utmParams } from "@/services/LazoLeadService";
import { LazoLoginResponse } from "@/types/LazoLoginResponse";
import { OAuthRedirectUser } from "@/types/OAuthRedirectUser";
import { envIsOneOf } from "@/utils/EnvironmentUtils";
import { hardNavigate, hardReplace } from "@/utils/hardNavigate";
import { getLandingPageByUser } from "@/utils/NavigationUtils";
import { recoverSessionFromCookie } from "@/utils/recoverSessionFromCookie";
import { isWorkspaceHost, PostLoginRedirectStorageKey } from "@/utils/RedirectUtils";
import { CheckCircleOutline, EmailOutlined, Google, VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import { Box, Divider, FormLabel, Link, styled, TextField, textFieldClasses, Typography } from "@mui/material";
import { HttpStatusCode } from "axios";
import moment from "moment";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export function setCustomExpirationDate() {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  localStorage.setItem("t_expiration", moment(date).format("YYYY-MM-DDTHH:mm:ss.SSSZZ"));
}

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "1rem",
  width: "100%",
  maxWidth: "25.75rem",
  padding: "1.5rem",
  [theme.breakpoints.down("sm")]: {
    padding: "1rem",
    gap: "0.75rem",
  },
}));

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.5rem",
  ".title": {
    fontSize: "1.375rem",
    fontWeight: "500",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.125rem",
    },
  },
  ".subtitle": {
    fontSize: "0.875rem",
    fontWeight: "500",
    color: Colors.LIGHT_GRAY_7A7E82,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.75rem",
    },
  },
}));

const FormContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  gap: "1rem",
  [`& .${textFieldClasses.root}`]: {
    backgroundColor: "#fff",
  },
});

const StyledButton = styled(CommonButton)(({ theme }) => ({
  width: "98%",
  height: "3.313rem",
  fontSize: "0.875rem",
  fontWeight: "600",
  borderRadius: "0.5rem",
  [theme.breakpoints.down("sm")]: {
    height: "2.75rem",
    fontSize: "0.813rem",
  },
}));

const ForgotPasswordLink = styled(Link)({
  color: Colors.BLACK_151517,
  paddingLeft: "0.125rem",
  fontSize: "0.75rem",
  fontWeight: "500",
  transition: "all 0.2s ease",
  width: "fit-content",
  ":hover": {
    cursor: "pointer",
    color: Colors.LAZO_PURPLE,
  },
});

const ActionsContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  gap: "0.5rem",
});

function handleLoginError(response: ApiResponseV2<LazoLoginResponse | string>, t: (key: string) => string) {
  switch (response.getHttpStatusCode()) {
    case HttpStatusCode.NotFound: {
      NotificationService.warn(t("auth:login.errors.userNotFound"));
      break;
    }
    case HttpStatusCode.Unauthorized: {
      if (response.getMessage() === "No active companies") {
        NotificationService.warn(t("auth:login.errors.inactiveCompanies"));
      } else {
        NotificationService.warn(t("auth:login.errors.incorrectCredentials"));
      }
      break;
    }
    default: {
      NotificationService.error(t("auth:login.errors.unexpectedError"));
      break;
    }
  }
}

export function onLoginSuccess(
  redirectUrl: string,
  otc: string,
  isMobile: boolean,
  navigateFn: (path: string) => void,
  t?: (key: string) => string
) {
  if (otc.length == 0) {
    const errorMessage = t ? t("login.errors.loginError") : "There was an error login in";
    NotificationService.error(errorMessage);
    return;
  }

  const url = `${redirectUrl}${LazoPaths.REDIRECT.substring(1)}`;
  const params = new URLSearchParams();
  params.set("otc", otc);

  const postRedirect = window.sessionStorage.getItem(PostLoginRedirectStorageKey);
  if (postRedirect) {
    window.sessionStorage.removeItem(PostLoginRedirectStorageKey);
    const storedUrl = new URL(postRedirect);
    // Never replay a /redirect?otc=… deeplink: its one-time code is already consumed, so the
    // exchange would fail and strand the user on the error path again (LS-1153).
    if (storedUrl.pathname !== LazoPaths.REDIRECT) {
      params.set("p", storedUrl.pathname + storedUrl.search);
    }
  }

  hardReplace(`${url}?${params}`);
}

const DebugLoginPanel = dynamic(() => (envIsOneOf(["qa", "dev"]) ? import("@/components/DebugLoginPanel") : Promise.resolve(() => null)), {
  ssr: false,
});

export function LazoLogin() {
  const { t } = useTranslation("auth");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState<boolean>(false);

  const requestMagicLinkMutation = useRequestMagicLink();

  const { register, control, handleSubmit, trigger, getValues } = useForm({ mode: "onChange" });

  const isMobileDevice = (): boolean => {
    return window.matchMedia && window.matchMedia("(max-width: 767px)").matches;
  };

  const handleEnterKey = (ev: KeyboardEvent) => {
    if (ev.code === "Enter") {
      trigger().then((r) => {
        if (r) {
          const email = getValues("email");
          const password = getValues("password");
          if (email && password) {
            onSubmit({ password: password, email: email });
          }
        }
      });
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleEnterKey);
    return () => document.removeEventListener("keydown", handleEnterKey);
  }, [handleEnterKey]);

  useEffect(() => {
    const queryParams: URLSearchParams = new URLSearchParams(window.location.search);
    if (queryParams.size === 0) {
      return;
    }

    const redirect = queryParams.get("redirect");
    if (redirect) {
      window.sessionStorage.setItem(PostLoginRedirectStorageKey, window.location.origin + redirect);
    }

    const token: string = queryParams.get("t");
    const u = queryParams.get("u");
    const utmParams: utmParams = {
      utmSource: queryParams.get("utm_source") || "",
      utmMedium: queryParams.get("utm_medium") || "",
      utmCampaign: queryParams.get("utm_campaign") || "",
      utmTerm: queryParams.get("utm_term") || "",
      utmContent: queryParams.get("utm_content") || "",
    };
    if (token && u) {
      setLoading(true);

      const user: OAuthRedirectUser = JSON.parse(atob(u));

      LazoAuthService.getRedirectUrl(user.externalId, token)
        .then((r) => {
          r.onSuccess((c) => {
            onLoginSuccess(c.redirectUrl, c.otc, isMobileDevice(), navigate, t);
          }).onError(() => {
            NotificationService.warn(t("login.errors.userNotFound"));
          });
        })
        .finally(() => setLoading(false));
    }
    if (utmParams) {
      sessionStorage.setItem("utmParams", JSON.stringify(utmParams));
    }
  }, []);

  // Cold-boot session recovery on a workspace subdomain: a user bounced to /login (e.g. after a
  // failed OTC handoff) may still hold a valid Domain=.lazo.one session cookie set moments earlier
  // by /login or /otc — recover it instead of asking for credentials again (LS-1153). The server
  // gates GET /session by that cookie, so an intentionally logged-out visitor (expired cookie)
  // just sees the form. Skipped on the root app hosts, where login must hand off to the workspace
  // via OTC, and during the OAuth redirect flow (t & u params), which logs in by itself.
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get("t") && queryParams.get("u")) return;
    if (!isWorkspaceHost(window.location.hostname)) return;
    if (AuthService.isLogged()) return;

    setLoading(true);
    recoverSessionFromCookie()
      .then((recovered) => {
        if (!recovered) return;

        let destination: string | undefined;
        const storedRedirect = window.sessionStorage.getItem(PostLoginRedirectStorageKey);
        if (storedRedirect) {
          window.sessionStorage.removeItem(PostLoginRedirectStorageKey);
          const storedUrl = new URL(storedRedirect);
          // Never replay /redirect?otc=…: the one-time code is already consumed.
          if (storedUrl.pathname !== LazoPaths.REDIRECT) {
            destination = storedUrl.pathname + storedUrl.search;
          }
        }
        if (!destination) {
          const user = AuthService.getUser();
          destination = (user && getLandingPageByUser(user)) || LazoPaths.TASKS;
        }

        // Full reload so App re-runs its auth bootstrap with the recovered session.
        hardNavigate(destination);
      })
      .finally(() => setLoading(false));
  }, []);

  const navigate = useNavigate();

  function onSubmit(data: { email: string; password: string }) {
    const { email, password } = data;
    setLoading(true);
    LazoAuthService.validateLogin(email, password)
      .then((r) => {
        r.onSuccess((c) => {
          onLoginSuccess(c.redirectUrl, c.otc, isMobileDevice(), navigate, t);
        }).onError((_, r) => handleLoginError(r, t));
      })
      .finally(() => {
        setTimeout(() => setLoading(false), 250);
      });
  }

  function handleMagicLinkRequest() {
    trigger("email").then((isValid) => {
      if (!isValid) {
        NotificationService.warn(t("login.form.magicLink.validationError"));
        return;
      }
      const redirectFromUrl = new URLSearchParams(window.location.search).get("redirect");
      const storedRedirect = window.sessionStorage.getItem(PostLoginRedirectStorageKey);
      const redirect = redirectFromUrl ?? (storedRedirect ? new URL(storedRedirect).pathname + new URL(storedRedirect).search : undefined);
      requestMagicLinkMutation.mutate({ email: getValues("email"), redirect });
    });
  }

  return (
    <>
      <LanguageSelector />
      <Container>
        {/* Guaranteed to only be sent to dev and qa environments */}
        {/* <DebugLoginPanel
          onLoginSuccess={(redirectUrl: string, otc: string, isMobile: boolean, navigateFn: (path: string) => void) =>
            onLoginSuccess(redirectUrl, otc, isMobile, navigateFn, t)
          }
          isMobileDevice={isMobileDevice}
          navigate={navigate}
        /> */}
        <Header>
          <Typography className={"title"}>{t("login.title")}</Typography>
          <Typography className={"subtitle"}>{t("login.subtitle")}</Typography>
        </Header>
        <FormContainer>
          <CommonFormControl>
            <FormLabel>{t("login.form.email.label")}</FormLabel>
            <Controller
              control={control}
              name={"email"}
              defaultValue={""}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  {...register("email", {
                    required: true,
                    pattern: {
                      value:
                        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: t("login.form.email.invalid"),
                    },
                  })}
                  value={value}
                  onChange={onChange}
                  error={!!error}
                  placeholder={t("login.form.email.placeholder")}
                />
              )}
            />
          </CommonFormControl>
          <CommonFormControl>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", px: "0.5rem" }}>
              <FormLabel sx={{ padding: 0 }}>{t("login.form.password.label")}</FormLabel>
              <ForgotPasswordLink onClick={() => setForgotPasswordOpen(true)}>{t("login.form.forgotPassword")}</ForgotPasswordLink>
            </Box>
            <Controller
              control={control}
              name={"password"}
              defaultValue={""}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  {...register("password", { required: true })}
                  InputProps={{
                    endAdornment: showPassword ? (
                      <VisibilityOffOutlined sx={{ ":hover": { cursor: "pointer" } }} onClick={() => setShowPassword(false)} />
                    ) : (
                      <VisibilityOutlined sx={{ ":hover": { cursor: "pointer" } }} onClick={() => setShowPassword(true)} />
                    ),
                  }}
                  value={value}
                  onChange={onChange}
                  error={!!error}
                  placeholder={t("login.form.password.placeholder")}
                  type={showPassword ? "text" : "password"}
                />
              )}
            />
          </CommonFormControl>
          <StyledButton disabled={loading} onClick={handleSubmit(onSubmit)} sx={{ mt: "0.25rem" }}>
            {loading ? <Spinner variant={"indeterminate"} /> : <>{t("login.form.submitButton")}</>}
          </StyledButton>
        </FormContainer>

        <ActionsContainer>
          <Divider sx={{ width: "100%", fontSize: "0.75rem", color: Colors.LIGHT_GRAY_7A7E82 }}>or</Divider>
          <StyledButton href={environment.baseURL + "/oauth/connect/google"} startIcon={<Google />} disabled={loading} variant="outlined">
            {t("login.form.googleButton")}
          </StyledButton>
          {requestMagicLinkMutation.isSuccess ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", color: Colors.LAZO_PURPLE, py: "0.5rem" }}>
              <CheckCircleOutline fontSize="small" />
              <Typography sx={{ fontSize: "0.875rem", fontWeight: "500", color: "inherit" }}>
                {t("login.form.magicLink.successMessage")}
              </Typography>
            </Box>
          ) : (
            <StyledButton
              startIcon={requestMagicLinkMutation.isPending ? null : <EmailOutlined />}
              disabled={loading || requestMagicLinkMutation.isPending}
              variant="outlined"
              onClick={handleMagicLinkRequest}
            >
              {requestMagicLinkMutation.isPending ? <Spinner variant={"indeterminate"} size={20} /> : t("login.form.magicLink.button")}
            </StyledButton>
          )}
        </ActionsContainer>
      </Container>
      {forgotPasswordOpen && <ForgotPasswordModal open={forgotPasswordOpen} onClose={() => setForgotPasswordOpen(false)} />}
    </>
  );
}
